// src/pages/DietAnalysis.jsx
import { useState, useEffect, useMemo } from "react";
import { 
  getAllStudents, subscribeDietLogs, getStudent, subscribeProgressLogs,
  subscribeCategories, DEFAULT_CATEGORIES
} from "../firebase/database";
import { todayStr, getWeekRange, formatDate, HHMMToMinutes, minutesToHHMM } from "../utils/helpers";
import { theme } from "../utils/theme";
import { useAuth } from "../context/AuthContext";
import { format, subDays, parseISO } from "date-fns";

const C = theme.colors;

export default function DietAnalysis({ studentId: kioskStudentId }) {
  const { user, userData } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(kioskStudentId || "");
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [dietLogs, setDietLogs] = useState([]);
  const [progressLogs, setProgressLogs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("daily"); // "daily" | "weekly"

  const isKiosk = !!kioskStudentId;
  const isFullAdmin = userData?.role === "admin" || userData?.role === "trainer";

  useEffect(() => {
    const unsub = subscribeCategories(setCategories);
    return unsub;
  }, []);

  useEffect(() => {
    if (isKiosk) return;

    getAllStudents().then(list => {
      const activeList = list.filter(s => s.status !== "inactive");
      setStudents(activeList);

      if (isFullAdmin) {
        if (activeList.length > 0 && !selectedStudentId) {
          const defaultStudent = activeList.find(s => s.name.includes("Pankaj Ojha") && (s.roll === "12" || s.roll === "012"));
          setSelectedStudentId(defaultStudent ? defaultStudent.id : activeList[0].id);
        }
      } else {
        // Instructor/Staff: Default to self
        const selfId = userData?.studentId || user?.uid;
        if (selfId && !selectedStudentId) {
          setSelectedStudentId(selfId);
        }
      }
    });
  }, [isKiosk, userData, user, isFullAdmin, selectedStudentId]);

  useEffect(() => {
    if (selectedStudentId) {
      getStudent(selectedStudentId).then(setProfile);
      const unsubDiet = subscribeDietLogs(selectedStudentId, (logs) => {
        setDietLogs(logs);
      }, 2000);
      const unsubProgress = subscribeProgressLogs(selectedStudentId, (logs) => {
        setProgressLogs(logs);
      });
      setLoading(false);
      return () => {
        unsubDiet();
        unsubProgress();
      };
    }
  }, [selectedStudentId]);

  const weekRange = useMemo(() => getWeekRange(selectedDate), [selectedDate]);
  
  const dailyStats = useMemo(() => {
    const stats = {};
    dietLogs.forEach(log => {
      const d = log.date;
      if (!d) return;
      if (!stats[d]) stats[d] = { consumed: 0, burned: 0 };
      stats[d].consumed += (parseFloat(log.kcalPerUnit || 0) * parseFloat(log.qty || 0));
    });
    progressLogs.forEach(log => {
      const d = log.date;
      if (!d) return;
      if (!stats[d]) stats[d] = { consumed: 0, burned: 0 };
      stats[d].burned += parseFloat(log.kcalBurned || 0);
    });
    return stats;
  }, [dietLogs, progressLogs]);

  const bmr = useMemo(() => {
    if (!profile) return 1800; // Default BMR
    const w = parseFloat(profile.weight || 70);
    const hFt = parseFloat(profile.heightFeet || 5);
    const hIn = parseFloat(profile.heightInches || 7);
    const totalInches = (hFt * 12) + hIn;
    const heightCm = totalInches * 2.54;
    
    let age = 25;
    if (profile.dob) {
      const birthDate = new Date(profile.dob);
      age = new Date().getFullYear() - birthDate.getFullYear();
    }
    
    if (profile.gender === "Female") {
      return Math.round((10 * w) + (6.25 * heightCm) - (5 * age) - 161);
    }
    return Math.round((10 * w) + (6.25 * heightCm) - (5 * age) + 5);
  }, [profile]);

  const target = profile?.dailyKcalTarget || (bmr + 500);

  const chartData = useMemo(() => {
    return weekRange.map(date => ({
      date,
      label: format(parseISO(date), "EEE"),
      value: dailyStats[date]?.consumed || 0,
      burned: (dailyStats[date]?.burned || 0) + bmr
    }));
  }, [weekRange, dailyStats, bmr]);

  const maxVal = Math.max(...chartData.map(d => d.value), target, 1000);

  const CHART_HEIGHT = 160;

  const navigateDate = (dir) => {
    const days = activeTab === "weekly" ? 7 : 1;
    const current = parseISO(selectedDate);
    const next = dir === "prev" ? subDays(current, days) : subDays(current, -days);
    setSelectedDate(format(next, "yyyy-MM-dd"));
  };

  const currentDailyLogs = useMemo(() => progressLogs.filter(l => l.date === selectedDate), [progressLogs, selectedDate]);
  const currentActivityBurned = dailyStats[selectedDate]?.burned || 0;
  const currentDailyBurned = bmr + currentActivityBurned;
  const currentDailyConsumed = dailyStats[selectedDate]?.consumed || 0;
  const netKcal = currentDailyConsumed - currentDailyBurned;
  const diffKcal = currentDailyConsumed - target;
  const variancePct = target > 0 ? Math.round((diffKcal / target) * 100) : 0;

  const timelineRows = useMemo(() => {
    const sorted = [...currentDailyLogs].sort((a, b) => HHMMToMinutes(a.startTime) - HHMMToMinutes(b.startTime));
    const rows = [];
    sorted.forEach(log => {
      const start = HHMMToMinutes(log.startTime);
      let placed = false;
      for (let i = 0; i < rows.length; i++) {
        const lastInRow = rows[i][rows[i].length - 1];
        if (HHMMToMinutes(lastInRow.endTime) <= start) {
          rows[i].push(log);
          placed = true;
          break;
        }
      }
      if (!placed) rows.push([log]);
    });
    return rows;
  }, [currentDailyLogs]);

  const hours = Array.from({ length: 25 }, (_, i) => i);

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif", color: C.text }}>
      <style>{`
        .diet-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; gap: 16px; }
        .diet-controls { display: flex; gap: 12px; align-items: center; }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .weekly-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 24px; }
        .chart-box { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 16px; padding: 24px; overflow-x: auto; }
        .table-box { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 16px; overflow-x: auto; margin-bottom: 24px; }
        .timeline-box { background: ${C.card}; border: 1px solid ${C.border}; border-radius: 16px; padding: 40px 24px; position: relative; margin-bottom: 24px; overflow-x: auto; }

        @media (max-width: 1024px) {
          .stats-grid { grid-template-columns: 1fr 1fr; }
          .weekly-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .diet-header { flex-direction: column; align-items: flex-start; }
          .diet-controls { width: 100%; justify-content: space-between; flex-wrap: wrap; }
          .stats-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="diet-header">
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>Diet & Nutrition Analysis</div>
          <div style={{ fontSize: 14, color: C.muted }}>Tracking calorie intake vs targets</div>
        </div>
        <div className="diet-controls">
          <div style={{ display: "flex", background: C.bg, borderRadius: 10, padding: 4, border: `1px solid ${C.border}` }}>
             <button 
              onClick={() => setActiveTab("daily")}
              style={{ background: activeTab === "daily" ? C.accent : "none", color: activeTab === "daily" ? "#fff" : C.text, border: "none", padding: "6px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            >
              Daily Kcal
            </button>
            <button 
              onClick={() => setActiveTab("weekly")}
              style={{ background: activeTab === "weekly" ? C.accent : "none", color: activeTab === "weekly" ? "#fff" : C.text, border: "none", padding: "6px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            >
              Weekly Trends
            </button>
          </div>
          <div style={{ display: "flex", background: C.bg, borderRadius: 10, padding: 4, border: `1px solid ${C.border}` }}>
            <button 
              onClick={() => navigateDate("prev")}
              style={{ background: "none", border: "none", padding: "6px 12px", cursor: "pointer", fontSize: 18, color: C.text }}
            >
              ←
            </button>
            <div style={{ padding: "6px 12px", fontSize: 13, fontWeight: 600, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>
              {activeTab === "weekly" ? `${formatDate(weekRange[0])} - ${formatDate(weekRange[6])}` : formatDate(selectedDate)}
            </div>
            <button 
              onClick={() => navigateDate("next")}
              style={{ background: "none", border: "none", padding: "6px 12px", cursor: "pointer", fontSize: 18, color: C.text }}
            >
              →
            </button>
          </div>
          {isFullAdmin && !isKiosk ? (
            <select 
              value={selectedStudentId} 
              onChange={e => setSelectedStudentId(e.target.value)}
              style={{ padding: "10px 14px", borderRadius: 10, border: `1px solid ${C.border}`, outline: "none", background: "#fff" }}
            >
              <option value="">Select Student</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll})</option>)}
            </select>
          ) : !isKiosk && (
            <div style={{ padding: "10px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: "#fff", fontSize: 14, fontWeight: 600, color: C.text }}>
              {students.find(s => s.id === selectedStudentId)?.name || userData?.name || "My Analysis"}
            </div>
          )}
        </div>
      </div>

      {activeTab === "daily" && (
        <div className="fadeUp">
          <div className="stats-grid">
            <div style={{ background: C.card, padding: 16, borderRadius: 12, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>TOTAL BURNED</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#ef4444" }}>{Math.round(currentDailyBurned)} kcal</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                BMR: {bmr} + Extra Activity: {Math.round(currentActivityBurned)}
              </div>
            </div>
            <div style={{ background: C.card, padding: 16, borderRadius: 12, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>TOTAL INTAKE</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>{Math.round(currentDailyConsumed)} kcal</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Today's Food Log</div>
            </div>
            <div style={{ background: C.card, padding: 16, borderRadius: 12, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>NET BALANCE</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: netKcal > 0 ? "#ef4444" : "#16A349" }}>{Math.round(netKcal)} kcal</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Intake - Burned</div>
            </div>
            <div style={{ background: C.card, padding: 16, borderRadius: 12, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>GOAL VARIANCE</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: Math.abs(diffKcal) < 100 ? "#16A349" : "#ef4444" }}>
                {diffKcal > 0 ? "+" : ""}{Math.round(diffKcal)} kcal
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>vs Target ({target})</div>
            </div>
          </div>

          <div className="timeline-box">
            <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, paddingBottom: 10, marginBottom: 20, minWidth: 600 }}>
              {hours.map(h => <div key={h} style={{ flex: 1, textAlign: "left", fontSize: 11, color: C.muted, borderLeft: `1px solid ${C.border}`, paddingLeft: 4, height: 10 }}>{String(h).padStart(2, "0")}:00</div>)}
            </div>
            <div style={{ position: "relative", minHeight: Math.max(160, timelineRows.length * 50), minWidth: 600 }}>
              {hours.map(h => <div key={h} style={{ position: "absolute", left: `${(h / 24) * 100}%`, top: 0, bottom: 0, width: 1, background: C.border, opacity: 0.5 }} />)}
              {timelineRows.map((row, rowIndex) => (
                <div key={rowIndex} style={{ position: "relative", height: 40, marginBottom: 12 }}>
                  {row.map(log => {
                    const startMin = HHMMToMinutes(log.startTime);
                    const endMin = HHMMToMinutes(log.endTime);
                    const left = (startMin / 1440) * 100;
                    const width = ((endMin - startMin) / 1440) * 100;
                    const cat = categories.find(c => c.id === log.category);
                    return (
                      <div key={log.entryId} style={{ position: "absolute", left: `${left}%`, width: `${width}%`, height: "100%", background: cat?.color || C.accent, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9, fontWeight: 600, padding: "0 4px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", zIndex: 1, overflow: "hidden" }}>
                        <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", textAlign: "center" }}>{log.topicName}</div>
                        <div style={{ fontSize: 8, opacity: 0.9 }}>{log.kcalBurned || 0} kcal</div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {currentDailyLogs.length === 0 && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 14 }}>No activity logs for this day.</div>}
            </div>
            <div style={{ display: "flex", borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 20, minWidth: 600 }}>
              {hours.map(h => <div key={h} style={{ flex: 1, textAlign: "left", fontSize: 11, color: C.muted, borderLeft: `1px solid ${C.border}`, paddingLeft: 4, height: 10 }}>{String(h).padStart(2, "0")}:00</div>)}
            </div>
          </div>

          <div className="table-box">
            <div style={{ padding: 16, background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700 }}>
              Activity Burn Log
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted }}>Activity / Topic</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Time</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Duration</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Extra Burn</th>
                </tr>
              </thead>
              <tbody>
                {currentDailyLogs.length > 0 ? (
                  currentDailyLogs.map((log, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 600 }}>{log.topicName}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, color: C.muted }}>{log.startTime} - {log.endTime}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13 }}>{minutesToHHMM(log.loggedMinutes)}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: "#ef4444" }}>{Math.round(log.kcalBurned || 0)} kcal</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" style={{ padding: 24, textAlign: "center", color: C.muted }}>No activity logs found for this day.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="table-box" style={{ marginBottom: 0 }}>
            <div style={{ padding: 16, background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700 }}>
              Diet Consumption Log
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted }}>Food Item</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Meal Time</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Qty</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Kcal/Unit</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Total Intake</th>
                </tr>
              </thead>
              <tbody>
                {dietLogs.filter(l => l.date === selectedDate).length > 0 ? (
                  dietLogs.filter(l => l.date === selectedDate).map((log, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 600 }}>{log.foodName}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, color: C.muted }}>{log.mealTime}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13 }}>{log.qty}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, color: C.muted }}>{log.kcalPerUnit}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: C.accent }}>{log.kcalPerUnit * log.qty} kcal</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="5" style={{ padding: 24, textAlign: "center", color: C.muted }}>No diet logs found for this day.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "weekly" && (
        <div className="fadeUp">
          <div className="weekly-grid">
            {/* Left: Daily Summary Card */}
            <div style={{ background: C.card, padding: 24, borderRadius: 16, border: `1px solid ${C.border}` }}>
              <h3 style={{ marginBottom: 20, fontSize: 18 }}>Weekly Summary</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>DAILY TARGET</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.green }}>{target} kcal</div>
                </div>
                <div style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>AVG. CONSUMED</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.accent }}>
                    {Math.round(chartData.reduce((a, b) => a + b.value, 0) / 7)} kcal
                  </div>
                </div>
                <div style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>AVG. TOTAL BURNED</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#ef4444" }}>
                    {Math.round(chartData.reduce((a, b) => a + b.burned, 0) / 7)} kcal
                  </div>
                </div>
                <div style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>BMI STATUS</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.purple }}>{profile?.bmi || "N/A"}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>Weight: {profile?.weight}kg | Height: {profile?.heightFeet}'{profile?.heightInches}"</div>
                </div>
              </div>
            </div>

            {/* Right: Weekly Chart */}
            <div className="chart-box">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 30, minWidth: 400 }}>
                <h3 style={{ fontSize: 18 }}>Calorie Trends</h3>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: C.accent }} />
                    <span style={{ fontSize: 12, color: C.muted }}>Taken</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: "#ef4444", border: "1px dashed #ef4444" }} />
                    <span style={{ fontSize: 12, color: C.muted }}>Target</span>
                  </div>
                </div>
              </div>

              <div style={{ height: CHART_HEIGHT + 40, display: "flex", alignItems: "flex-end", gap: 12, position: "relative", paddingBottom: 30, borderBottom: `1px solid ${C.border}`, minWidth: 400 }}>
                {/* Target Line */}
                <div style={{ 
                  position: "absolute", 
                  left: 0, right: 0, 
                  bottom: `${(target / maxVal) * CHART_HEIGHT + 30}px`, 
                  borderTop: "2px dashed #ef4444", 
                  zIndex: 1,
                  opacity: 0.5
                }}>
                  <span style={{ position: "absolute", right: 0, top: -18, fontSize: 10, color: "#ef4444", fontWeight: 700 }}>TARGET</span>
                </div>

                {chartData.map((d, i) => {
                  const barHeight = (d.value / maxVal) * CHART_HEIGHT;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, height: "100%", justifyContent: "flex-end" }}>
                      <div style={{ 
                        width: "100%", 
                        maxWidth: 40, 
                        height: `${barHeight}px`, 
                        background: d.value > target ? "#ef4444" : C.accent, 
                        borderRadius: "6px 6px 0 0",
                        transition: "height 0.3s ease",
                        position: "relative"
                      }}>
                        {d.value > 0 && (
                          <div style={{ position: "absolute", top: -20, left: "50%", transform: "translateX(-50%)", fontSize: 10, fontWeight: 700 }}>
                            {d.value}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, position: "absolute", bottom: -25 }}>{d.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="table-box" style={{ marginTop: 24 }}>
            <div style={{ padding: 16, background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700 }}>
              Weekly Log History
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted }}>Date</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Consumed</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Burned</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Net</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Target</th>
                  <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 12, color: C.muted }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {[...weekRange].reverse().map(date => {
                  const consumed = dailyStats[date]?.consumed || 0;
                  const activityBurned = dailyStats[date]?.burned || 0;
                  const totalBurned = activityBurned + bmr;
                  const net = consumed - totalBurned;
                  const diff = consumed - target;
                  const pct = target > 0 ? Math.round((diff / target) * 100) : 0;
                  
                  return (
                    <tr key={date} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 700 }}>{formatDate(date)}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: C.accent }}>{Math.round(consumed)} kcal</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: "#ef4444" }}>{Math.round(totalBurned)} kcal</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800 }}>{Math.round(net)} kcal</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", color: C.muted }}>{target} kcal</td>
                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        <span style={{ 
                          padding: "4px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                          background: diff > 0 ? "#FEF2F2" : "#F0FDF4",
                          color: diff > 0 ? "#EF4444" : "#16A349"
                        }}>
                          {diff > 0 ? `+${Math.round(diff)}` : Math.round(diff)} ({pct}%)
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
